import {
  ArrowDropDown,
  ArrowDropUp,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank,
  Search,
} from "@mui/icons-material";
import { Button, Checkbox, Grid, TextField, InputAdornment } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";

/**
 * @component MultipleSelect
 * @description Componente para seleção múltipla de opções
 * @param {Object} props - Propriedades do componente
 * @param {string} props.label - Label do componente
 * @param {Array} props.options - Opções disponíveis
 * @param {Array} props.selectedItems - Itens selecionados
 * @param {Function} props.onSelectionChange - Função para selecionar itens
 * @param {Function} props.onApply - Função para aplicar filtros
 * @param {string} props.searchPlaceholder - Placeholder do campo de busca
 * @param {string} props.maxHeight - Altura máxima da lista de opções
 * @param {string} props.listMaxHeight - Altura máxima da lista de opções
 * @param {string} props.width - Largura do componente
 * @param {string} props.minWidth - Largura mínima do componente
 * @param {string} props.dataAttribute - Atributo de dados do componente
 * @param {boolean} props.showSearch - Mostrar campo de busca
 * @param {boolean} props.showOkButton - Mostrar botão de OK
 * @param {Function} props.getOptionLabel - Função para obter o label das opções
 * @param {Function} props.getOptionValue - Função para obter o valor das opções
 * @param {Function} props.isOptionEqualToValue - Função para verificar se o item é igual ao valor
 * @returns {JSX.Element} Componente de seleção múltipla de opções
 */
const MultipleSelect = ({
  label = "Selecionar item(s)",
  options = [],
  selectedItems = [],
  onSelectionChange,
  onApply,
  searchPlaceholder = "Buscar...",
  maxHeight = "300px",
  listMaxHeight = "200px",
  width = "100%",
  minWidth = { xs: "100%", sm: "250px" },
  dataAttribute = "multiple-select",
  showSearch = true,
  showOkButton = true,
  showAllByDefault = false,
  getOptionLabel = (option) => option.name || option.label || option,
  getOptionValue = (option) => {
    if (typeof option === "object" && option !== null) {
      return (
        option.id || option.idcustomer || option.value || JSON.stringify(option)
      );
    }
    return String(option);
  },
  isOptionEqualToValue = (option, value) =>
    getOptionValue(option) === getOptionValue(value),
}) => {
  const [showList, setShowList] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [isAllByDefault, setIsAllByDefault] = useState(showAllByDefault && selectedItems.length === 0);

  // Filtra opções baseado no texto de busca
  const filteredOptions = useMemo(
    () => {
      const filtered = options.filter((option) => {
        const label = getOptionLabel(option).toLowerCase();
        const matches = label.includes(searchText.toLowerCase());
        return matches;
      });
      
      console.log("Opções filtradas:", filtered.length);
      return filtered;
    },
    [options, searchText, getOptionLabel]
  );

  // Fecha a lista ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showList &&
        !event.target.closest(`[data-${dataAttribute}]`) &&
        !event.target.closest(".multiple-select-dropdown")
      ) {
        setShowList(false);
        setSearchText("");
      }
    };

    if (showList) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [showList, dataAttribute]);

  const handleItemClick = (option) => {
    const isSelected = selectedItems?.some((item) =>
      isOptionEqualToValue(item, option)
    );
    let newSelection;

    if (isSelected) {
      newSelection = selectedItems.filter(
        (item) => !isOptionEqualToValue(item, option)
      );
    } else {
      newSelection = [...(selectedItems || []), option];
    }

    if (onSelectionChange) {
      onSelectionChange(newSelection);
    }
  };

  const handleOkClick = () => {
    setShowList(false);
    setSearchText("");
    if (onApply) {
      onApply(selectedItems);
    }
  };

  const handleClearClick = () => {
    setSearchText("");
    if (onSelectionChange) {
      onSelectionChange([]);
    }
  };

  return (
    <Grid item xs={12} sm={6} md={3} lg={2}>
      <div
        style={{
          position: "relative",
          width: "100%",
          zIndex: 1,
        }}
        data-multiple-select
      >
        <TextField
          size="small"
          label={`${label}`}
          value={
            selectedItems?.length
              ? `${selectedItems.length} item(s) selecionado(s)`
              : ""
          }
          onChange={(e) => setSearchText(e.target.value)}
          onClick={() => setShowList(!showList)}
          sx={{
            width: width,
            minWidth: minWidth,
            "& .MuiOutlinedInput-root": {
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "rgba(0, 0, 0, 0.87)",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#1976d2",
                borderWidth: "2px",
              },
            },
            "& .MuiInputLabel-root": {
              "&.Mui-focused": {
                color: "#1976d2",
              },
            },
          }}
          InputProps={{
            readOnly: true,
            endAdornment: (
              <span>
                {!showList ? (
                  <ArrowDropDown
                    style={{
                      cursor: "pointer",
                      color: "rgba(0, 0, 0, 0.54)",
                      hover: {
                        border: "1px solid #ccc",
                      },
                    }}
                  />
                ) : (
                  <ArrowDropUp
                    style={{
                      cursor: "pointer",
                      color: "rgba(0, 0, 0, 0.54)",
                      hover: {
                        border: "1px solid #ccc",
                      },
                    }}
                  />
                )}
              </span>
            ),
          }}
        />

        {showList && (
          <div
            style={{
              position: "absolute",
              top: "100%",
              left: 0,
              right: 0,
              backgroundColor: "white",
              border: "1px solid #ccc",
              borderRadius: "4px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
              zIndex: 9999,
              maxHeight: maxHeight,
              overflow: "hidden",
              minWidth: "100%",
              maxWidth: "100%",
              boxSizing: "border-box",
            }}
            className="multiple-select-dropdown"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            {/* Campo de busca */}
            {showSearch && (
              <div style={{ 
                padding: "8px 0px", 
                borderBottom: "1px solid #eee",
                boxSizing: "border-box",
                width: "100%",
                paddingLeft: "8px",
                paddingRight: "8px",
              }}>
                <TextField
                  size="small"
                  placeholder={searchPlaceholder}
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  fullWidth
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Search style={{ 
                          color: "rgba(0, 0, 0, 0.54)",
                          fontSize: "20px"
                        }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ 
                    width: "100%",
                    "& .MuiOutlinedInput-root": {
                      width: "100%",
                      "& fieldset": {
                        border: "1px solid #ccc",
                      },
                    },
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                  }}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                  }}
                />
              </div>
            )}

            {/* Lista de opções */}
            <div style={{ maxHeight: listMaxHeight, overflow: "auto" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: "8px 16px",
                  cursor: "pointer",
                  fontSize: "14px",
                  textAlign: "left",
                  borderBottom: "1px solid #eee",
                  fontWeight: "500",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  
                  // Se está no estado "todos por padrão"
                  if (isAllByDefault) {
                    // Desmarcar todos (sair do estado "todos por padrão")
                    setIsAllByDefault(false);
                    if (onSelectionChange) {
                      onSelectionChange([]);
                    }
                    return;
                  }

                  const allSelected = filteredOptions.every((option) =>
                    selectedItems?.some((item) =>
                      isOptionEqualToValue(item, option)
                    )
                  );

                  if (allSelected) {
                    // Desmarcar todos
                    const newSelection = selectedItems.filter(
                      (item) =>
                        !filteredOptions.some((option) =>
                          isOptionEqualToValue(item, option)
                        )
                    );
                    if (onSelectionChange) {
                      onSelectionChange(newSelection);
                    }
                  } else {
                    // Selecionar todos
                    const newSelection = [...(selectedItems || [])];
                    filteredOptions.forEach((option) => {
                      if (
                        !newSelection.some((item) =>
                          isOptionEqualToValue(item, option)
                        )
                      ) {
                        newSelection.push(option);
                      }
                    });
                    if (onSelectionChange) {
                      onSelectionChange(newSelection);
                    }
                  }
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = "#f5f5f5";
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = "transparent";
                }}
              >
                <Checkbox
                  icon={<CheckBoxOutlineBlank fontSize="small" />}
                  checkedIcon={<CheckBoxIcon fontSize="small" />}
                  style={{ marginRight: 8 }}
                  checked={isAllByDefault || filteredOptions.every((option) =>
                    selectedItems?.some((item) =>
                      isOptionEqualToValue(item, option)
                    )
                  )}
                />
                <span
                  style={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    textAlign: "left",
                    flex: 1,
                  }}
                >
                  {isAllByDefault ? "Desmarcar todos" : "Todos"}
                </span>
              </div>

              {filteredOptions.map((option, index) => {
                const isSelected = selectedItems?.some((item) =>
                  isOptionEqualToValue(item, option)
                );
                return (
                  <div
                    key={`option-${index}-${getOptionValue(option)}`}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      padding: "8px 16px",
                      cursor: "pointer",
                      fontSize: "14px",
                      textAlign: "left",
                      backgroundColor: (isAllByDefault || isSelected) ? "#f0f0f0" : "transparent",
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      // Se está no estado "todos por padrão", sair dele e selecionar apenas este item
                      if (isAllByDefault) {
                        setIsAllByDefault(false);
                        if (onSelectionChange) {
                          onSelectionChange([option]);
                        }
                      } else {
                        handleItemClick(option);
                      }
                    }}
                    onMouseEnter={(e) => {
                      if (!isSelected && !isAllByDefault) {
                        e.target.style.backgroundColor = "#f5f5f5";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected && !isAllByDefault) {
                        e.target.style.backgroundColor = "transparent";
                      } else if (isAllByDefault) {
                        e.target.style.backgroundColor = "#f0f0f0";
                      }
                    }}
                  >
                    <Checkbox
                      icon={<CheckBoxOutlineBlank fontSize="small" />}
                      checkedIcon={<CheckBoxIcon fontSize="small" />}
                      style={{ marginRight: 8 }}
                      checked={isAllByDefault || isSelected}
                    />
                    <span
                      style={{
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        textAlign: "left",
                        flex: 1,
                      }}
                    >
                      {getOptionLabel(option)}
                    </span>
                  </div>
                );
              })}
            </div>
            {(selectedItems && selectedItems.length > 0) || showOkButton ? (
              <div
                style={{
                  padding: "8px",
                  borderTop: "1px solid #eee",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                {showOkButton && (
                  <Button
                    size="small"
                    variant="contained"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleOkClick();
                    }}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    sx={{
                      minWidth: "80px",
                      fontSize: "12px",
                      backgroundColor: "#1976d2",
                      color: "#fff",
                      "&:hover": {
                        backgroundColor: "#1565c0",
                      },
                      "&:active": {
                        backgroundColor: "#0d47a1",
                      },
                      textTransform: "none",
                      fontWeight: 500,
                    }}
                  >
                    OK
                  </Button>
                )}
              </div>
            ) : null}
          </div>
        )}
      </div>
    </Grid>
  );
};

export default MultipleSelect;
